import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { useCreateQuote, useUpdateQuote, useQuote } from "@/hooks/useQuotes";
import { useEvent } from "@/hooks/useEvents";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import { useToast } from "@/hooks/useToast";

const quoteSchema = z.object({
  client_name: z.string().optional(),
  client_address: z.string().optional(),
  service_description: z.string().optional(),
  validity_days: z.coerce.number().min(1).max(365),
  deposit_percentage: z.coerce.number().min(0).max(100),
  transport_cost: z.coerce.number().min(0),
  accommodation_cost: z.coerce.number().min(0),
  meals_cost: z.coerce.number().min(0),
  material_cost: z.coerce.number().min(0),
  artist_fees: z.coerce.number().min(0),
  general_fees: z.coerce.number().min(0),
  other_cost: z.coerce.number().min(0),
  notes: z.string().optional(),
});

type QuoteFormData = z.infer<typeof quoteSchema>;

interface QuoteFormProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

function generateServiceDescription(event: {
  name: string;
  event_type: string[];
  event_date: string;
  location: string;
}): string {
  const types = event.event_type.join(", ");
  return `Prestation musicale pour ${event.name}\nType : ${types}\nLieu : ${event.location}`;
}

export function QuoteForm({ open, onClose, eventId }: QuoteFormProps) {
  const { data: existingQuote } = useQuote(eventId);
  const { data: event } = useEvent(eventId);
  const { data: settings } = useOrganizationSettings();
  const createQuote = useCreateQuote();
  const updateQuote = useUpdateQuote();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<QuoteFormData>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      client_name: "",
      client_address: "",
      service_description: "",
      validity_days: 30,
      deposit_percentage: 30,
      transport_cost: 0,
      accommodation_cost: 0,
      meals_cost: 0,
      material_cost: 0,
      artist_fees: 0,
      general_fees: 0,
      other_cost: 0,
      notes: "",
    },
  });

  useEffect(() => {
    if (!open) return;

    if (existingQuote) {
      reset({
        client_name: existingQuote.client_name || "",
        client_address: existingQuote.client_address || "",
        service_description: existingQuote.service_description || "",
        validity_days:
          existingQuote.validity_days || settings?.default_validity_days || 30,
        deposit_percentage:
          existingQuote.deposit_percentage ||
          settings?.default_deposit_percentage ||
          30,
        transport_cost: existingQuote.transport_cost || 0,
        accommodation_cost: existingQuote.accommodation_cost || 0,
        meals_cost: existingQuote.meals_cost || 0,
        material_cost: existingQuote.material_cost || 0,
        artist_fees: existingQuote.artist_fees || 0,
        general_fees: existingQuote.general_fees || 0,
        other_cost: existingQuote.other_cost || 0,
        notes: existingQuote.notes || "",
      });
    } else if (event) {
      const organizer = event.organizer;
      const addressParts = [
        organizer?.address,
        organizer?.email ? `Email: ${organizer.email}` : null,
        organizer?.phone ? `Tél: ${organizer.phone}` : null,
      ].filter(Boolean);

      reset({
        client_name: organizer?.name || "",
        client_address: addressParts.join("\n"),
        service_description: generateServiceDescription(event),
        validity_days: settings?.default_validity_days || 30,
        deposit_percentage: settings?.default_deposit_percentage || 30,
        transport_cost: 0,
        accommodation_cost: 0,
        meals_cost: 0,
        material_cost: 0,
        artist_fees: 0,
        general_fees: 0,
        other_cost: 0,
        notes: "",
      });
    }
  }, [open, existingQuote, event, settings, reset]);

  const values = watch();
  const total =
    (values.transport_cost || 0) +
    (values.accommodation_cost || 0) +
    (values.meals_cost || 0) +
    (values.material_cost || 0) +
    (values.artist_fees || 0) +
    (values.general_fees || 0) +
    (values.other_cost || 0);

  const deposit = total * ((values.deposit_percentage || 30) / 100);
  const balance = total - deposit;

  const onSubmit = async (data: QuoteFormData) => {
    try {
      if (existingQuote) {
        await updateQuote.mutateAsync({
          id: existingQuote.id,
          updates: {
            client_name: data.client_name || null,
            client_address: data.client_address || null,
            service_description: data.service_description || null,
            validity_days: data.validity_days,
            deposit_percentage: data.deposit_percentage,
            transport_cost: data.transport_cost,
            accommodation_cost: data.accommodation_cost,
            meals_cost: data.meals_cost,
            material_cost: data.material_cost,
            artist_fees: data.artist_fees,
            general_fees: data.general_fees,
            other_cost: data.other_cost,
            notes: data.notes || null,
          },
        });
        toast({ title: "Devis mis à jour" });
      } else {
        await createQuote.mutateAsync({
          event_id: eventId,
          client_name: data.client_name || null,
          client_address: data.client_address || null,
          service_description: data.service_description || null,
          validity_days: data.validity_days,
          deposit_percentage: data.deposit_percentage,
          transport_cost: data.transport_cost,
          accommodation_cost: data.accommodation_cost,
          meals_cost: data.meals_cost,
          material_cost: data.material_cost,
          artist_fees: data.artist_fees,
          general_fees: data.general_fees,
          other_cost: data.other_cost,
          notes: data.notes || null,
        });
        toast({ title: "Devis créé" });
      }
      onClose();
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingQuote ? "Modifier le devis" : "Créer un devis"}
          </DialogTitle>
          {existingQuote?.quote_number && (
            <p className="text-sm text-muted-foreground">
              N° {existingQuote.quote_number}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium">Client</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="client_name">
                  Nom du client / Organisateur
                </Label>
                <Input id="client_name" {...register("client_name")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_address">Adresse / Coordonnées</Label>
                <Textarea
                  id="client_address"
                  rows={3}
                  {...register("client_address")}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-medium">Description de la prestation</h3>
            <div className="space-y-2">
              <Textarea
                id="service_description"
                rows={4}
                placeholder="Détaillez la prestation : type d'événement, durée, programme..."
                {...register("service_description")}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-medium">Détail des coûts</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="transport_cost">Transport (€)</Label>
                  <HelpTooltip content="Frais de déplacement : carburant, péages, location de véhicule, billets de train/avion." />
                </div>
                <Input
                  id="transport_cost"
                  type="number"
                  step="0.01"
                  {...register("transport_cost")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="accommodation_cost">Hébergement (€)</Label>
                  <HelpTooltip content="Frais d'hôtel ou de logement pour l'équipe pendant l'événement." />
                </div>
                <Input
                  id="accommodation_cost"
                  type="number"
                  step="0.01"
                  {...register("accommodation_cost")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="meals_cost">Repas (€)</Label>
                  <HelpTooltip content="Frais de restauration pour l'équipe sur place." />
                </div>
                <Input
                  id="meals_cost"
                  type="number"
                  step="0.01"
                  {...register("meals_cost")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="material_cost">Matériel (€)</Label>
                  <HelpTooltip content="Location ou achat de matériel : sonorisation, éclairage, instruments, décoration." />
                </div>
                <Input
                  id="material_cost"
                  type="number"
                  step="0.01"
                  {...register("material_cost")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="artist_fees">Cachets artistiques (€)</Label>
                  <HelpTooltip content="Rémunération des artistes et musiciens participant à l'événement." />
                </div>
                <Input
                  id="artist_fees"
                  type="number"
                  step="0.01"
                  {...register("artist_fees")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="general_fees">Frais généraux (€)</Label>
                  <HelpTooltip content="Frais de structure CBM : coordination, assurances, administration." />
                </div>
                <Input
                  id="general_fees"
                  type="number"
                  step="0.01"
                  {...register("general_fees")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="other_cost">Autres (€)</Label>
                  <HelpTooltip content="Dépenses diverses non couvertes par les autres catégories." />
                </div>
                <Input
                  id="other_cost"
                  type="number"
                  step="0.01"
                  {...register("other_cost")}
                />
              </div>

              <div className="flex items-end">
                <div className="rounded-lg bg-primary/10 p-3 w-full">
                  <p className="text-sm text-muted-foreground">Total HT</p>
                  <p className="text-xl font-bold">{total.toFixed(2)} €</p>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="font-medium">Conditions</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="validity_days">Validité (jours)</Label>
                  <HelpTooltip content="Durée pendant laquelle le devis reste valable après émission." />
                </div>
                <Input
                  id="validity_days"
                  type="number"
                  min={1}
                  max={365}
                  {...register("validity_days")}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="deposit_percentage">Acompte (%)</Label>
                  <HelpTooltip content="Pourcentage du montant total à verser à la signature du devis." />
                </div>
                <Input
                  id="deposit_percentage"
                  type="number"
                  min={0}
                  max={100}
                  {...register("deposit_percentage")}
                />
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Acompte à la signature ({values.deposit_percentage || 30}%)
                </span>
                <span className="font-medium">{deposit.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Solde avant l'événement
                </span>
                <span className="font-medium">{balance.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes internes</Label>
            <Textarea
              id="notes"
              placeholder="Notes visibles uniquement par l'équipe CBM..."
              {...register("notes")}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Enregistrement..."
                : existingQuote
                  ? "Mettre à jour"
                  : "Créer le devis"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
